import { useCallback, useEffect, useMemo, useState } from 'react';
import ReactTableWrapper from '../../components/ReactTableWrapper';
import ListPageLayout from '../../components/ListPageLayout';
import ListPageGradientSummary from '../../components/ListPageGradientSummary';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../Sidebar';
import Swal from 'sweetalert2';
import { useCookies } from 'react-cookie';
import moment from 'moment';
import { displayError } from '../../helpers';
import { exportAsCSV } from '../../utils/CSVExporter';
import { exportAsPDF } from '../../utils/PDFExporter';
import { getUserAllowedPlants } from '../../services/PlantServices';
import {
    cancelDieselConsumption,
    deleteDieselConsumption,
    getDieselConsumptionList,
} from '../../services/DieselConsumptionServices';
import { getStockStatement } from '../../services/ConsumptionNoteServices';
import { printDieselConsumption } from '../../utils/dieselConsumptionPrint';
import {
    getDateRangeFromSalesSettings,
    getSalesSettingsSummary,
    loadSalesListSettings,
    resolvePlantFilter,
    saveSalesListSettings,
} from '../../utils/salesListSettings';
import SalesListSettingsModal from '../../components/SalesListSettingsModal';
import { buildDieselStockSummaryCards } from './dieselStockUtils';

function DieselConsumptionTable() {
    const [data, setData] = useState({ records: [] });
    const [stockByProduct, setStockByProduct] = useState({});
    const navigate = useNavigate();
    const [cookies] = useCookies(['myToken', 'myUserId']);
    const [isLoading, setIsLoading] = useState(true);
    const [plants, setPlants] = useState([]);
    const [isInitialized, setIsInitialized] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [salesSettings, setSalesSettings] = useState(() => loadSalesListSettings(cookies.myUserId));

    const activeDateRange = useMemo(
        () => getDateRangeFromSalesSettings(salesSettings),
        [salesSettings],
    );

    const settingsSummary = useMemo(
        () => getSalesSettingsSummary(salesSettings, plants, [], { hideCustomers: true }),
        [salesSettings, plants],
    );

    const summaryCards = useMemo(
        () => buildDieselStockSummaryCards(stockByProduct),
        [stockByProduct],
    );

    useEffect(() => {
        getUserAllowedPlants(cookies).then((response) => {
            setPlants((response.plant_list || [])
                .filter((p) => p.status)
                .map((p) => ({
                    value: p.id,
                    label: p.plant_alias,
                })));
            setIsInitialized(true);
        });
    }, [cookies]);

    const loadStockSummary = useCallback(async (plantFilter) => {
        const asOnDate = moment().format('DD-MM-YYYY');
        let targets = [];

        if (plantFilter && plantFilter !== '*') {
            targets = String(plantFilter).split(',').map((id) => id.trim()).filter(Boolean);
        } else {
            targets = plants.map((p) => String(p.value));
        }

        if (!targets.length) {
            setStockByProduct({});
            return;
        }

        try {
            const responses = await Promise.all(
                targets.map((plantId) => getStockStatement(cookies, Number(plantId), asOnDate, 'cumulative')
                    .catch(() => ({ stock_statement: [] }))),
            );
            const map = {};
            responses.forEach((res) => {
                (res.stock_statement || []).forEach((row) => {
                    if (row.product_id == null) return;
                    const key = String(row.product_id);
                    if (!map[key]) {
                        map[key] = {
                            product_id: row.product_id,
                            product_name: row.product_name,
                            unit: row.unit,
                            received_qty: 0,
                            consumed_qty: 0,
                            closing_qty: 0,
                        };
                    }
                    map[key].received_qty += Number(row.received_qty) || 0;
                    map[key].consumed_qty += Number(row.consumed_qty) || 0;
                    map[key].closing_qty += Number(row.closing_qty) || 0;
                });
            });
            setStockByProduct(map);
        } catch {
            setStockByProduct({});
        }
    }, [cookies, plants]);

    const fetchRecords = useCallback(() => {
        const plantId = resolvePlantFilter(salesSettings, plants);
        const [rangeFrom, rangeTo] = activeDateRange;
        if (!plantId || !rangeFrom?.isValid() || !rangeTo?.isValid()) {
            setIsLoading(false);
            return Promise.resolve();
        }
        setIsLoading(true);
        return getDieselConsumptionList(cookies, {
            from_date: rangeFrom.format('DD-MM-YYYY'),
            upto_date: rangeTo.format('DD-MM-YYYY'),
            plant_id: plantId,
        })
            .then((response) => {
                setData({
                    records: (response.diesel_consumption_list || []).map((row) => {
                        const isActive = row.status === true || row.status === 1 || row.status === 'Active';
                        return {
                            ...row,
                            consumption_no_display: row.consumption_no,
                            status_label: isActive ? 'Active' : 'Cancelled',
                            hide_row_edit: isActive ? 'No' : 'Yes',
                            hide_row_cancel: isActive ? 'No' : 'Yes',
                            hide_row_delete: isActive ? 'Yes' : 'No',
                            hide_row_print: 'No',
                        };
                    }),
                });
                return loadStockSummary(plantId);
            })
            .catch((error) => {
                displayError(error?.response?.data || { message: 'Failed to load diesel consumption notes.' }, 'Load Failed');
                setData({ records: [] });
                setStockByProduct({});
            })
            .finally(() => setIsLoading(false));
    }, [activeDateRange, cookies, plants, salesSettings, loadStockSummary]);

    useEffect(() => {
        if (isInitialized) {
            fetchRecords();
        }
    }, [fetchRecords, isInitialized]);

    const columns = useMemo(() => [
        { fieldName: 'plant_alias', headerName: 'Plant' },
        { fieldName: 'consumption_no_display', headerName: 'Consumption No' },
        { fieldName: 'consumption_date', headerName: 'Date' },
        { fieldName: 'equipment_summary', headerName: 'Equipment' },
        { fieldName: 'total_diesel_qty', headerName: 'Diesel Qty', align: 'right' },
        { fieldName: 'issued_by', headerName: 'Issued By' },
        { fieldName: 'status_label', headerName: 'Status' },
        { fieldName: 'id', headerName: 'ROW_ACTIONS' },
    ], []);

    const exportColumns = useMemo(
        () => columns.filter((col) => col.headerName !== 'ROW_ACTIONS'),
        [columns],
    );

    const handleOpen = (id) => {
        const row = data.records.find((r) => String(r.id) === String(id));
        const isActive = row?.status_label === 'Active';
        navigate(isActive ? `/DieselConsumptionEdit/${id}` : `/DieselConsumptionView/${id}`);
    };

    const handleCancel = (id) => {
        Swal.fire({
            title: 'Cancel diesel consumption?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, cancel',
        }).then((result) => {
            if (!result.isConfirmed) return;
            cancelDieselConsumption(cookies, id)
                .then(() => {
                    Swal.fire('Cancelled', 'Diesel consumption cancelled.', 'success');
                    fetchRecords();
                })
                .catch((error) => displayError(error?.response?.data || error, 'Cancel Failed'));
        });
    };

    const handleDelete = (id) => {
        Swal.fire({
            title: 'Delete diesel consumption?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete',
        }).then((result) => {
            if (!result.isConfirmed) return;
            deleteDieselConsumption(cookies, id)
                .then(() => {
                    Swal.fire('Deleted', 'Record deleted.', 'success');
                    fetchRecords();
                })
                .catch((error) => displayError(error?.response?.data || error, 'Delete Failed'));
        });
    };

    const handlePrint = (id) => {
        printDieselConsumption(cookies, id).catch((error) => {
            displayError(error?.response?.data || error, 'Print Failed');
        });
    };

    const handleCSVExport = () => {
        exportAsCSV('diesel_consumption_notes', exportColumns, data.records);
    };

    const handlePDFExport = () => {
        exportAsPDF('Diesel Consumption Notes', exportColumns, data.records);
    };

    return (
        <>
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <div id="page-wrap">
                <ListPageLayout
                    moduleLabel="STORE"
                    title="Diesel Consumption Notes"
                    formLink="/DieselConsumption"
                    addNewLabel="Add New"
                    recordCount={data.records.length}
                    settingsSummary={settingsSummary}
                    onOpenSettings={() => setShowSettings(true)}
                    onExportCSV={handleCSVExport}
                    onExportPDF={handlePDFExport}
                    hideRecordSummary
                    modeLabel=""
                >
                    <ListPageGradientSummary cards={summaryCards} />
                    <ReactTableWrapper
                        data={data.records}
                        columns={columns}
                        isLoading={isLoading}
                        onRowEdit={handleOpen}
                        onRowCancel={handleCancel}
                        onRowDelete={handleDelete}
                        onRowPrint={handlePrint}
                        onCSVExport={handleCSVExport}
                        onPDFExport={handlePDFExport}
                        hidePrint={false}
                        compactRows
                        autoPinActionColumn={false}
                    />
                </ListPageLayout>
            </div>
            <SalesListSettingsModal
                show={showSettings}
                onClose={() => setShowSettings(false)}
                allowedPlants={plants}
                initialSettings={salesSettings}
                hideCustomers
                title="General Settings"
                onSave={(next) => {
                    const saved = saveSalesListSettings(cookies.myUserId, next);
                    setSalesSettings(saved);
                    setShowSettings(false);
                }}
            />
        </>
    );
}

export default DieselConsumptionTable;
